/**
 * Cookie Consent Management
 * GDPR-compliant cookie consent handling
 */

export interface CookieConsent {
  essential: boolean; // Always true, cannot be disabled
  analytical: boolean; // Google Analytics
  marketing: boolean; // Tracking pixels
  timestamp: number; // When consent was given
}

const STORAGE_KEY = 'vesselly-consent';

/**
 * Get current cookie consent from localStorage
 */
export function getCookieConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const consent = JSON.parse(stored) as CookieConsent;
    // Ensure essential is always true
    return {
      ...consent,
      essential: true,
    };
  } catch {
    return null;
  }
}

/**
 * Save cookie consent to localStorage
 */
export function saveCookieConsent(consent: Omit<CookieConsent, 'timestamp'>): void {
  if (typeof window === 'undefined') return;
  
  const consentWithTimestamp: CookieConsent = {
    ...consent,
    essential: true, // Always true
    timestamp: Date.now(),
  };
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consentWithTimestamp));
    
    // Dispatch custom event for other components to react
    window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: consentWithTimestamp }));
  } catch (error) {
    console.error('Failed to save cookie consent:', error);
  }
}

/**
 * Check if user has given consent (any consent, including just essential)
 */
export function hasConsent(): boolean {
  return getCookieConsent() !== null;
}

/**
 * Check if analytical cookies are allowed
 */
export function hasAnalyticalConsent(): boolean {
  const consent = getCookieConsent();
  return consent?.analytical === true;
}

/**
 * Check if marketing cookies are allowed
 */
export function hasMarketingConsent(): boolean {
  const consent = getCookieConsent();
  return consent?.marketing === true;
}

/**
 * Clear cookie consent (for testing or user request)
 */
export function clearCookieConsent(): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: null }));
  } catch (error) {
    console.error('Failed to clear cookie consent:', error);
  }
}

/**
 * Open cookie settings (triggered from Cookie Policy page)
 * This will be handled by the CookieBanner component
 */
export function openCookieSettings(): void {
  if (typeof window === 'undefined') return;
  
  window.dispatchEvent(new CustomEvent('open-cookie-settings'));
}


